import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, FormControl, Grid } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { AptorApi, useAptorApi } from '../../../../Api';
import { IGridQueryRequest, OrderDirection, PropertyError } from '../../../../Api/AptorApi';
import { BackButton, Loading, Panel } from '../../../../Components';
import { SelectField } from '../../../../Components/Field';
import { TypedFormFieldValue, SelectOption } from '../../../../Components/Form/Form.types';
import { UserContext } from '../../../../Context/UserContext/UserContext';
import { useApiValidationSnackbar } from '../../../../Api/useValidationSnackbar';
import { useStyles } from '../../LawPortal.styles';
import { StepButtonBar } from './OtherRequirementWizard/StepButtonBar';
import { Checklist, IListItem } from '../../../AptorAdmin/Components/Checklist/Checklist.List/Checklist.List';
import { ChecklistForm } from '../../../AptorAdmin/Components/Checklist/Checklist.Form';

interface IRequirementChecklist {
  requirement: string;
  items: IListItem[];
}

export const RequirementComplianceQuestions = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const id = parseInt(idParam || '0');
  const { api, abortController } = useAptorApi();
  const { getCompanyUnitName } = useContext(UserContext);
  const [companyUnits, setCompanyUnits] = useState<SelectOption[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<SelectOption[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(true);

  useEffect(() => {
    const request: IGridQueryRequest = { page: 0, pageSize: 100, orderBy: 'name', orderDirection: OrderDirection.Ascending };
    api.searchRequirementCompanyUnits<any>(id, request).then((result) => {
      if (abortController.current.signal.aborted) return;
      const options = (result?.data || []).map((unit: any) => ({ label: unit.name, value: unit.id }));
      setCompanyUnits(options);
      // single unit hai to seedha checklist par bhej do, dropdown skip
      if (options.length === 1) {
        setSelectedUnits(options);
      }
      setLoadingUnits(false);
    });
  }, [id]);

  const handleUnitSelect = useCallback((units: SelectOption | SelectOption[]) => {
    setSelectedUnits(Array.isArray(units) ? units : [units]);
  }, []);

  const shouldBackToUnitSelection = companyUnits.length > 1 && selectedUnits.length > 0;

  if (loadingUnits) {
    return <Loading />;
  }

  return (
    <>
      <Grid item xs={12}>
        {shouldBackToUnitSelection ? (
          <BackButton disableMargin link="" onclick={() => setSelectedUnits([])} />
        ) : (
          <BackButton disableMargin link="/law-portal/other-requirements/" />
        )}
      </Grid>
      <Grid item md={9} xs={12}>
        {selectedUnits.length === 0 ? (
          <CompanyUnitSelection options={companyUnits} onNext={handleUnitSelect} getCompanyUnitName={getCompanyUnitName} />
        ) : (
          <ChecklistEditor reqId={id} unitId={selectedUnits[0].value as number} selectedUnits={selectedUnits} />
        )}
      </Grid>
    </>
  );
};

const CompanyUnitSelection = ({
  options,
  onNext,
  getCompanyUnitName,
}: {
  options: SelectOption[];
  onNext: (units: SelectOption | SelectOption[]) => void;
  getCompanyUnitName: (formatMessage: any, isSingular: boolean) => string;
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const [selected, setSelected] = useState<SelectOption | SelectOption[]>();

  const handleChange = useCallback(
    (value: TypedFormFieldValue<number> | TypedFormFieldValue<number>[] | undefined) => {
      setSelected(value as SelectOption | SelectOption[] | undefined);
    },
    [],
  );

  return (
    <Panel autoHeight>
      <Box className={classes.unitSelectionRow}>
        <FormControl className={classes.unitSelectControl}>
          <SelectField
            name="companyUnit"
            placeholder={formatMessage(
              { id: 'law-portal.manage.update-requirements.dialog.companyUnit.placeholder' },
              { alias: getCompanyUnitName(formatMessage, true) },
            )}
            options={options}
            errorTexts={[]}
            initialState={selected}
            onChange={handleChange}
          />
        </FormControl>
        <Box className={classes.nextButtonOffset}>
          <StepButtonBar
            onCancel={() => {}}
            onNext={() => selected && onNext(selected)}
            nextLabel="form.next"
            nextDisabled={!selected || (Array.isArray(selected) && selected.length === 0)}
            formatMessage={formatMessage}
            showBack={false}
          />
        </Box>
      </Box>
    </Panel>
  );
};

const ChecklistEditor = ({
  reqId,
  unitId,
  selectedUnits,
}: {
  reqId: number;
  unitId: number;
  selectedUnits: SelectOption[];
}) => {
  const { formatMessage } = useIntl();
  const { api, abortController } = useAptorApi();
  const { notifySuccess, notifyUnhandledException, notifyValidationErrors } = useApiValidationSnackbar();
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [law, setLaw] = useState<string>();
  const [items, setItems] = useState<IListItem[]>([]);
  const [errors, setErrors] = useState<PropertyError[]>([]);

  useEffect(() => {
    setLoading(true);
    api.getRequirementChecklist<IRequirementChecklist>(reqId, unitId).then((checklist) => {
      if (abortController.current.signal.aborted) return;
      setLaw(checklist.requirement);
      setItems(checklist.items ?? []);
      setLoading(false);
    });
  }, [reqId, unitId]);

  const addQuestion = (name: string) => setItems((prev) => [...prev, { name, type: 'question', groupItems: [] }]);
  const addGroup = (name: string) => setItems((prev) => [...prev, { name, type: 'group', groupItems: [] }]);

  const enableSave = useMemo(() => items.some((i) => i.type === 'question' || i.groupItems.length > 0), [items]);

  const submit = async () => {
    const request = { items };
    const action = (i: AptorApi) =>
      Promise.all(selectedUnits.map((unit) => i.editRequirementChecklist(reqId, unit.value as number, request))).then(
        () => notifySuccess(),
      );
    const onValidation = (errs: PropertyError[]) => {
      notifyValidationErrors(errs);
      setErrors(errs);
    };
    setIsSaving(true);
    try {
      await api.invoke(action, abortController.current, onValidation, notifyUnhandledException);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Panel autoHeight titleKey="admin.law-portal.law.checklist.heading" titleValues={{ name: law }}>
        <ChecklistForm addGroup={addGroup} addQuestion={addQuestion} errors={errors} />
      </Panel>
      <Panel autoHeight titleKey="admin.law-portal.law.checklist.list.title" style={{ marginTop: 10 }}>
        <Checklist items={items} updateItems={setItems} />
      </Panel>
      <Box mt={2}>
        <StepButtonBar
          onCancel={() => {}}
          onNext={submit}
          nextLabel="form.save"
          nextDisabled={!enableSave || isSaving}
          isLoading={isSaving}
          formatMessage={formatMessage}
          showBack={false}
        />
      </Box>
    </>
  );
};